import { marked } from 'marked';
import TurndownService from 'turndown';

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

/** Detects if a string contains meaningful HTML tags (not just entities). */
const HTML_TAG_RE = /<(b|strong|i|em|u|ins|s|del|strike|a|br|p|div|span|ul|ol|li|pre|code|blockquote|h[1-6]|tg-spoiler)[\s/>]/i;
const ANY_HTML_TAG_RE = /<\/?[a-zA-Z][^>]*>/;

// ─────────────────────────────────────────────────────────────────────────────
// Turndown — HTML → Markdown
// ─────────────────────────────────────────────────────────────────────────────

const td = new TurndownService({
    headingStyle: 'atx',
    codeBlockStyle: 'fenced',
    emDelimiter: '_',
    hr: '---',
    bulletListMarker: '-',
});

// Keep our own escape minimal — we want Markdown chars to pass through
td.escape = (str: string) => str;

// Paragraphs → double newline (standard Markdown to preserve paragraph spacing)
td.addRule('paragraph', {
    filter: 'p',
    replacement: (content) => content ? '\n\n' + content + '\n\n' : '',
});

// Divs → single newline (soft break as produced by contentEditable)
td.addRule('softParagraph', {
    filter: 'div',
    replacement: (content) => content ? '\n' + content : '',
});

// <br> → \n
td.addRule('lineBreak', {
    filter: 'br',
    replacement: () => '\n',
});

// Bold → *text*
td.addRule('bold', {
    filter: ['strong', 'b'],
    replacement: (content) => content.trim() ? `*${content.trim()}*` : content,
});

// Italic → _text_
td.addRule('italic', {
    filter: ['em', 'i'],
    replacement: (content) => content.trim() ? `_${content.trim()}_` : content,
});

// Strikethrough → ~text~
td.addRule('strikethrough', {
    filter: ['s', 'del', 'strike'] as any,
    replacement: (content) => content.trim() ? `~${content.trim()}~` : content,
});

// Underline → __text__
td.addRule('underline', {
    filter: ['u', 'ins'] as any,
    replacement: (content) => content.trim() ? `__${content.trim()}__` : content,
});

// Spoiler spans → ||text||
td.addRule('spoiler', {
    filter: (node: any) =>
        (node.nodeName === 'SPAN' && (node.hasAttribute('data-spoiler') || node.classList?.contains('tg-spoiler') || node.classList?.contains('spoiler-text'))) ||
        node.nodeName === 'TG-SPOILER',
    replacement: (content) => content.trim() ? `||${content.trim()}||` : content,
});

// Links → [text](url) — preserve only href, drop all other attrs
td.addRule('link', {
    filter: 'a',
    replacement: (content, node: any) => {
        const href = node.getAttribute?.('href') || '';
        if (!href || href.startsWith('javascript:')) return content;
        const text = content.trim() || href;
        return `[${text}](${href})`;
    },
});

// Lists — use bullet symbols Telegram understands
td.addRule('listItem', {
    filter: 'li',
    replacement: (content, node: any) => {
        const parent = node.parentNode;
        const isOrdered = parent?.nodeName === 'OL';
        const index = isOrdered
            ? Array.from(parent.children).indexOf(node) + 1
            : null;
        const prefix = isOrdered ? `${index}. ` : '- ';
        return prefix + content.trim().replace(/\n/g, '\n  ') + '\n';
    },
});
td.addRule('list', {
    filter: ['ul', 'ol'],
    replacement: (content) => '\n' + content.trim() + '\n',
});

// ─────────────────────────────────────────────────────────────────────────────
// Pre-processing helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Unescapes ChatGPT-style pre-escaped Markdown characters.
 * ChatGPT often outputs: \* \_ \~ \` \[ \] etc.
 */
function unescapeChatGptMarkdown(text: string): string {
    return text.replace(/\\([_*~`|[\]()>#+\-={}.!])/g, '$1');
}

/**
 * Normalizes line endings and literal "\n" strings.
 */
function normalizeNewlines(text: string): string {
    // Literal 4-char sequence \\n (escaped twice) → \n
    text = text.replace(/\\\\n/g, '\n');
    // Literal 2-char sequence \n (ChatGPT JSON-stringified) → \n
    text = text.replace(/\\n/g, '\n');
    // Windows CRLF → LF
    text = text.replace(/\r\n/g, '\n');
    // Standalone CR → LF
    text = text.replace(/\r/g, '\n');
    return text;
}

/**
 * Collapses excessive blank lines (max 2 consecutive) and trims.
 */
function collapseNewlines(text: string): string {
    return text.replace(/\n{3,}/g, '\n\n').trim();
}

/**
 * Strips style/script/head HTML elements entirely (content too).
 */
function stripNonContentTags(html: string): string {
    return html
        .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
        .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
        .replace(/<head[^>]*>[\s\S]*?<\/head>/gi, '')
        .replace(/<!--[\s\S]*?-->/g, '');
}

/**
 * Normalizes tg-spoiler / data-spoiler spans to a canonical form Turndown can pick up.
 */
function normalizeSpoilerSpans(html: string): string {
    // <span class="tg-spoiler">…</span> → <span data-spoiler>…</span>
    return html
        .replace(/<span\s+class="tg-spoiler"[^>]*>/gi, '<span data-spoiler>')
        .replace(/<tg-spoiler[^>]*>/gi, '<span data-spoiler>')
        .replace(/<\/tg-spoiler>/gi, '</span>');
}

// ─────────────────────────────────────────────────────────────────────────────
// Public API
// ─────────────────────────────────────────────────────────────────────────────

/**
 * THE key export.
 *
 * Accepts ANY input — raw Markdown, HTML, mixed content, ChatGPT output with
 * escaped chars, plain text with literal "\n" sequences — and returns a clean
 * Markdown string in our Telegram dialect:
 *   *bold*, _italic_, ~strikethrough~, ||spoiler||, __underline__,
 *   `code`, ```block```, [text](url)
 *
 * This is the ONLY function that should be called when converting user input
 * to the internal-state Markdown string.
 */
export function anyInputToMarkdown(input: string): string {
    if (!input || typeof input !== 'string') return '';

    // Step 1: normalize newline sequences and ChatGPT escaping
    let text = normalizeNewlines(input);
    text = unescapeChatGptMarkdown(text);

    // Step 2: detect HTML and run through Turndown if found
    if (HTML_TAG_RE.test(text) || ANY_HTML_TAG_RE.test(text)) {
        // Strip non-content elements (style, script, etc.)
        let html = stripNonContentTags(text);
        html = normalizeSpoilerSpans(html);

        // Decode HTML entities so Turndown doesn't re-escape them
        html = decodeHtmlEntities(html);

        try {
            text = td.turndown(html);
        } catch {
            // If Turndown fails on truly malformed HTML, fall back to tag stripping
            text = html.replace(/<[^>]+>/g, '');
        }

        // Re-run newline cleanup after Turndown
        text = normalizeNewlines(text);
    }

    // Step 3: final cleanup
    return collapseNewlines(text);
}

/**
 * Converts stored Markdown to HTML suitable for rendering inside a
 * contentEditable div. The output is *display-only* HTML—it should NEVER
 * be sent to any API. Uses our Markdown-to-HTML mapping:
 *   *bold* → <b>, _italic_ → <i>, ~strike~ → <s>, ||spoiler|| → <span data-spoiler>,
 *   __underline__ → <u>, `code` → <code>, ```block``` → <pre>, [text](url) → <a>
 */
export function markdownToEditorHtml(markdown: string): string {
    if (!markdown || typeof markdown !== 'string') return '';

    let processed = markdown;

    // 1. Pre-convert spoiler markers before Marked sees them (it won't know ||…||)
    processed = processed.replace(/\|\|([\s\S]*?)\|\|/g, '<span data-spoiler>$1</span>');

    // 2. Underline: __text__ — do before bold so __ isn't consumed as two _
    processed = processed.replace(/__([\s\S]*?)__/g, '<u>$1</u>');

    // 3. Convert **bold** → *bold* for Marked consistency, then let Marked handle *bold*
    //    Actually we use Marked's built-in GFM bold (**) — but our dialect uses *single*
    //    So we convert our single-star bold to double before passing to Marked:
    //    *text* (bold) → **text** for Marked, _text_ → _text_ (italic, Marked handles this)
    //    We must be careful not to convert _italic words_ into bold!
    //    Strategy: replace *…* (not **…**) with <b>…</b> ourselves.
    processed = processed.replace(/(?<!\*)\*(?!\*)(?!\s)([\s\S]*?)(?<!\s)\*(?!\*)/g, '<b>$1</b>');

    // 4. Strikethrough ~text~ → <s> (Marked uses ~~, we use single ~)
    processed = processed.replace(/~([\s\S]*?)~/g, '<s>$1</s>');

    // 5. Now run through Marked for: _italic_, `code`, ```blocks```, [links](url), newlines, lists
    const html = marked.parse(processed, {
        async: false,
        breaks: true,
        gfm: true,
        pedantic: false,
    }) as unknown as string;

    return html.trim();
}

/**
 * Converts editor HTML back to our internal Markdown.
 * Used by the editor's `handleInput` to serialize the contentEditable content.
 * This is NOT for arbitrary HTML — only for HTML that was generated by
 * `markdownToEditorHtml` or produced by the browser's contentEditable.
 */
export function htmlToMarkdown(html: string): string {
    if (!html || typeof html !== 'string') return '';
    return anyInputToMarkdown(html);
}

// ─────────────────────────────────────────────────────────────────────────────
// Internal helpers
// ─────────────────────────────────────────────────────────────────────────────

function decodeHtmlEntities(text: string): string {
    return text
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/&nbsp;/g, ' ')
        .replace(/&#(\d+);/g, (_, code) => String.fromCharCode(parseInt(code, 10)))
        .replace(/&#x([0-9a-fA-F]+);/g, (_, code) => String.fromCharCode(parseInt(code, 16)));
}

// ─────────────────────────────────────────────────────────────────────────────
// Legacy exports (kept for backward compat with SocialPreview / old callers)
// ─────────────────────────────────────────────────────────────────────────────

/** @deprecated Use markdownToEditorHtml */
export const markdownToHtml = markdownToEditorHtml;

/** @deprecated Use anyInputToMarkdown */
export const htmlToMarkdownLegacy = htmlToMarkdown;

/**
 * Converts internal Markdown to Telegram-compatible HTML (for HTML parse_mode).
 * NOTE: We now prefer MarkdownV2, so this is used only for preview/display in old paths.
 */
export const markdownToTelegramHtml = (markdown: string): string => {
    if (!markdown) return '';
    let html = markdownToEditorHtml(markdown);

    // Transliterate spoiler span to Telegram-canonical spoiler
    html = html.replace(/<span data-spoiler>(.*?)<\/span>/gs, '<span class="tg-spoiler">$1</span>');

    // Convert block tags to clean newlines (Telegram HTML parse_mode doesn't support <p>)
    html = html
        .replace(/<\/p>/g, '\n')
        .replace(/<p>/g, '')
        .replace(/<\/div>/g, '\n')
        .replace(/<div>/g, '')
        .replace(/<br\s*\/?>/g, '\n');

    // Remove empty formatting tags
    let prev: string;
    do {
        prev = html;
        html = html.replace(/<(b|strong|i|em|u|ins|s|strike|del|span)[^>]*>\s*<\/\1>/g, '');
        html = html.replace(/<(b|strong|i|em|u|ins|s|strike|del|span)[^>]*>(\s+)<\/\1>/g, '$2');
    } while (html !== prev);

    html = html.replace(/\n{6,}/g, '\n\n\n\n\n');

    return html.trim();
};
